import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { uniqueId } from 'lodash';
import SortArrowsIcon from '../Icons/SortArrows.icon';
import { SortTypes } from '../../types/sort.types';

export interface TableHeaderItemOrderProps {
  dataProperty: string;
  sort?: 'asc' | 'desc' | 'default';
}

export interface TableHeaderColumnType extends TableHeaderItemOrderProps {
  title: string | React.JSX.Element;
  sortable: boolean;
  width?: string;
}

export interface TableHeaderProps {
  columns: TableHeaderColumnType[];
  order?: TableHeaderItemOrderProps;
  onSort?: Function;
  type?: 'flex';
}

const StyledHeaderColumnText = styled.div`
  user-select: none;
  pointer-events: none;
  width: 100%;
`;
const StyledHeaderColumnComponent = styled.div`
  user-select: none;
  pointer-events: none;
  width: 100%;
`;
const StyledHeaderColumnClickable = css`
  cursor: pointer;
  transition: color 0.3s;
`;
const StyledHeaderColumn = styled.div<{
  isClickable: boolean;
  width?: string;
}>`
  display: flex;
  flex-shrink: 1;
  flex-grow: 1;
  gap: 10px;
  color: ${({ theme }) => theme.colors.LABEL};
  font-size: 12px;
  line-height: 14px;
  font-weight: 500;
  text-transform: uppercase;
  align-items: center;
  width: ${props => props.width || 'inherit'};
  padding: 18px 20px;
  ${props => props.isClickable && StyledHeaderColumnClickable};
`;
const StyledHeader = styled.div<{
  colCount: number;
  type?: 'flex';
}>`
  display: ${props => props.type || 'grid'};
  width: 100%;
  grid-template-columns: repeat(${props => props.colCount}, 1fr);
  border-bottom: 1px solid ${({ theme }) => theme.colors.BORDER};
`;

const TableHeaderComponent = ({ columns, order, onSort, type }: TableHeaderProps) => {
  const { t } = useTranslation();

  const handleSort = (sortOrder: TableHeaderItemOrderProps) => {
    if (!onSort) return;
    if (
      !order ||
      (order && order.sort === SortTypes.DEFAULT) ||
      order.dataProperty !== sortOrder.dataProperty
    ) {
      onSort({
        ...sortOrder,
        sort: SortTypes.ASC,
      });
      return;
    }

    onSort({
      ...sortOrder,
      sort: order.sort === SortTypes.ASC ? SortTypes.DESC : SortTypes.ASC,
    });
  };

  const sortOrder = (column: TableHeaderColumnType) => {
    if (order && order.dataProperty === column.dataProperty) {
      return order.sort || SortTypes.DEFAULT;
    }
    return SortTypes.DEFAULT;
  };

  return (
    <StyledHeader colCount={columns.length} type={type}>
      {columns.map((column: TableHeaderColumnType) => {
        return (
          <StyledHeaderColumn
            key={uniqueId('table-header-')}
            isClickable={column.sortable}
            onClick={() => column.sortable && order && handleSort(column)}
            width={column?.width}
          >
            {column.sortable && (
              <SortArrowsIcon sortOrder={sortOrder(column)} />
            )}
            {typeof column.title === 'string' ?
              (<StyledHeaderColumnText>{t(column.title)}</StyledHeaderColumnText>) :
              <StyledHeaderColumnComponent>{column.title}</StyledHeaderColumnComponent>
            }
          </StyledHeaderColumn>
        );
      })}
    </StyledHeader>
  );
};

export default TableHeaderComponent;
